import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { injectUrlViewState } from '../../../utils/url-view-state.util';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { RestaurantService } from '../../../services/restaurant.service';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../../utils/feature-flag.util';
import { getCurrencySymbol } from '../../../utils/currency-symbol.util';
import { MatDialog } from '@angular/material/dialog';
import {
  ManagePlanComponent,
  NEEDS_PAYMENT_METHOD,
} from './modal/manage-plan/manage-plan.component';
import { NextInvoicePreviewComponent } from './modal/next-invoice-preview/next-invoice-preview.component';
import { ToastrService } from 'ngx-toastr';
import { SubscriptionService } from '../../../services/subscription.service';
import { FNFResponse } from '../../../models/response';
import { NgxSpinnerService } from 'ngx-spinner';
import { billingTabFor } from '../../../utils/notification-format.util';
import { PriceLabel, periodEndAfter, priceLabel } from '../../../utils/plan-pricing.util';
import { hasQuantityInLabel, isZeroOpeningInvoice } from '../../../utils/invoice-display.util';
import { isLimitedAccessStatus } from '../../../utils/restaurant-status.util';
type SubTab = 'overview' | 'add-ons' | 'billing-history' | 'payment-method';
const SUB_TABS: readonly SubTab[] = ['overview', 'add-ons', 'billing-history', 'payment-method'];
type StatusTone = 'success' | 'warning';

interface AddOn {
  name: string;
  description: string;
  price: number;
  status: StatusTone;
  statusLabel: string;
}

type MarketplaceBadgeType =
  'active' | 'locked' | 'scheduled' | 'requested' | 'on-hold' | 'rejected';

/** A hardware add-on request, still with the Fork & Find team (FNF-1454). */
type HardwareRequestStatus = 'REQUESTED' | 'ON_HOLD' | 'REJECTED';

interface MarketplaceAddOn {
  addonId: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  gradient: string;
  iconColor: string;
  badge?: { label: string; type: MarketplaceBadgeType };
  price: number;
  priceUnit: string;
  tagPrice?: number;
  quantity?: number;
  isSubscribed: boolean;
  subscribedQuantity?: number;
  lockedMessage?: string;
  buttonLabel: string;
  buttonStyle: 'dark' | 'outline';
  buttonDisabled?: boolean;
  actionInProgress?: boolean;
  /** Hardware is requested, then delivered by our team; billing starts on delivery (FNF-1454). */
  isHardware?: boolean;
  requestStatus?: HardwareRequestStatus;
  requestReason?: string;
  /** A change asked for on delivered hardware: the new total (FNF-1464 / FNF-1465). */
  requestedQuantity?: number;
  /** Delivered hardware the restaurant asked to give back; billed until collected. */
  isReturn?: boolean;
  /** An open change on delivered hardware: the stepper stays on what is delivered. */
  quantityLocked?: boolean;
  /** "Returning 1 of 3 · all 3 billed until collected" and the like (FNF-1465). */
  pendingNote?: string;
}

type BillingHistoryStatus = 'paid' | 'draft' | 'void' | 'uncollectible';

interface BillingHistoryItem {
  /** USAGE: a period's per-order charge, billed after the period ends (FNF-1284). */
  type: 'PLAN' | 'ADDON' | 'USAGE';
  label: string;
}

interface BillingHistoryRow {
  /** The subscription service's invoice id — what a notification's `invoiceId` names. */
  invoiceId: string;
  invoiceNumber: string;
  billingPeriod: string;
  invoiceDate: string;
  items: BillingHistoryItem[];
  amount: number;
  status: BillingHistoryStatus;
  statusLabel: string;
  pdfUrl?: string;
  hostedUrl?: string;
}

@Component({
  selector: 'app-subscription',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './subscription.component.html',
  styleUrl: './subscription.component.css',
})
export class SubscriptionComponent implements OnInit, OnDestroy {
  restaurantService = inject(RestaurantService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private querySub?: Subscription;
  private refreshSub?: Subscription;
  protected readonly getCurrencySymbol = getCurrencySymbol;

  subscriptionService = inject(SubscriptionService);
  ngxSpinnerService = inject(NgxSpinnerService);
  plans: any = [];
  addons1: any = [];
  displayLoading: boolean = false;
  currentSubscription: any;
  currentSubscriptionPlan: any;
  cancellingPendingPlan = false;
  currentSubscriptionAddons: AddOn[] = [];
  currentRestaurant: any;
  restaurantId = '';
  restaurantLocation = '';
  currentPaymentMethod: any;
  currentInvoices: any;
  redirecting = false;
  billingUnavailable = false;
  subscriptionNotFound = false;
  subscriptionLoaded = false;

  activeTab: SubTab = 'overview';
  private urlState = injectUrlViewState();
  currency = 'USD';

  /**
   * The invoice a notification deep link asked for (`?invoiceId=`). Its row is
   * scrolled into view and highlighted once the history has loaded; the Stripe
   * receipt itself is left to the row's View button rather than opened in a
   * new tab, which popup blockers would eat.
   */
  highlightedInvoiceId: string | null = null;

  addOnsMarketplace: MarketplaceAddOn[] = [];

  private readonly addOnCategoryMeta: Record<
    string,
    { icon: string; gradient: string; iconColor: string; label: string }
  > = {
    HARDWARE: {
      icon: 'bx bx-devices',
      gradient: '#eaf2ff',
      iconColor: '#2f6fed',
      label: 'Hardware',
    },
    SOFTWARE: {
      icon: 'bx bx-code-alt',
      gradient: '#f3ecfd',
      iconColor: '#7c3aed',
      label: 'Software',
    },
    SERVICE: {
      icon: 'bx bx-support',
      gradient: 'var(--sub-success-bg)',
      iconColor: 'var(--sub-success)',
      label: 'Service',
    },
  };

  paymentMethod = {
    brand: 'VISA',
    last4: '4242',
    expires: '08/28',
    holderName: 'Kirusnabavani',
  };

  nextInvoicePreview: any;
  /** This period's orders and estimated per-order charge so far (FNF-1284); null for flat plans. */
  usage: any = null;

  billingHistory: BillingHistoryRow[] = [];
  displayAddon: boolean = false;
  /** False until the restaurant's feature flags arrive; ADD_ON is unknown before then. */
  private flagsLoaded = false;

  get recentInvoices(): BillingHistoryRow[] {
    return this.billingHistory.slice(0, 3);
  }

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {
    this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
      if (!restaurant) {
        return;
      }
      this.currentRestaurant = restaurant;
      this.restaurantId = restaurant.restaurant_id || '';
      const locality = restaurant.address?.address_components?.locality;
      this.restaurantLocation = locality
        ? `${locality.city}, ${locality.state?.long}`
        : restaurant.address?.formatted_address || '';
    });
    this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
      if (flags && !isFeatureActive(flags, FEATURE_FLAG_KEYS.BILLING)) {
        this.router.navigate(['/']);
        return;
      }
      this.flagsLoaded = !!flags;
      this.displayAddon = isFeatureActive(flags, FEATURE_FLAG_KEYS.ADD_ON);
      this.leaveAddOnsTabIfOff();
    });
    // this.getAllPlans();
    // this.getAllAddons();
    this.getCurrentSubscription();
    this.getCurrentPaymentMethod();
    this.getCurrentSubscriptionInvoices();
    this.getNextInvoice();

    // Notification deep links (FNF-592): `?tab=billing-history&invoiceId=…`
    // opens the receipt's row, `?tab=payment-method` the card. Watched rather
    // than read once so a second notification opened from this page moves the
    // highlight instead of being ignored.
    this.querySub = this.route.queryParamMap.subscribe((params) => {
      this.applyDeepLink(params.get('tab'), params.get('invoiceId'));
    });

    // A plan-change or billing push while this page is open (FNF-592): the
    // server moved on, so reload everything the page shows.
    this.refreshSub = this.restaurantService.subscriptionRefresh$.subscribe(() => {
      this.getCurrentSubscription();
      this.getCurrentPaymentMethod();
      this.getCurrentSubscriptionInvoices();
      this.getNextInvoice();
    });
  }

  ngOnDestroy() {
    this.querySub?.unsubscribe();
    this.refreshSub?.unsubscribe();
  }

  /**
   * A tab the user picked: shown, and written to the URL (?tab) so a browser
   * reload reopens it. A receipt highlight from a notification link is let go
   * with it, so it does not pull the page back to Billing History.
   */
  setActiveTab(tab: SubTab) {
    this.activeTab = tab;
    this.urlState.set('tab', tab, 'overview');
    this.urlState.set('invoiceId', null);
  }

  /**
   * Applies what a notification's route asked for. Unknown tabs are ignored,
   * and an `invoiceId` the history does not list leaves the tab open with no
   * highlight — never an error, the receipt may simply be older than the page.
   */
  applyDeepLink(tab: string | null, invoiceId: string | null): void {
    // Any tab the page has (a reload of a picked tab), else a notification's
    // spelling of one (FNF-592).
    const target = SUB_TABS.includes(tab as SubTab)
      ? (tab as SubTab)
      : billingTabFor(tab, invoiceId ?? undefined);
    if (target) this.activeTab = target;
    this.leaveAddOnsTabIfOff();
    this.highlightedInvoiceId = invoiceId || null;
    this.focusHighlightedInvoice();
  }

  /**
   * `?tab=add-ons` (a bookmark, a shared or deep link) must not open the
   * marketplace while the ADD_ON flag is off (FNF-1584): fall back to Overview
   * and drop the tab from the URL. Waits for the flags, which can land after
   * the URL is read; the template also hides the marketplace until then.
   */
  private leaveAddOnsTabIfOff(): void {
    if (this.flagsLoaded && !this.displayAddon && this.activeTab === 'add-ons') {
      this.activeTab = 'overview';
      this.urlState.set('tab', 'overview', 'overview');
    }
  }

  isHighlighted(invoice: BillingHistoryRow): boolean {
    return !!this.highlightedInvoiceId && invoice.invoiceId === this.highlightedInvoiceId;
  }

  /** DOM id of an invoice's row, so the deep link can scroll to it. */
  invoiceRowId(invoice: BillingHistoryRow): string {
    return `invoice-${invoice.invoiceId}`;
  }

  /**
   * Scrolls the highlighted row into view once it exists. Runs after the
   * query params change and again after the history loads, whichever is last;
   * the row is only in the DOM once both the tab is active and the rows are
   * built, hence the deferral to the next tick.
   */
  private focusHighlightedInvoice(): void {
    const id = this.highlightedInvoiceId;
    if (!id || !this.billingHistory.some((row) => row.invoiceId === id)) return;
    if (typeof document === 'undefined') return;
    setTimeout(() => {
      const row = document.getElementById(`invoice-${id}`);
      row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  incrementAddOnQuantity(addOn: MarketplaceAddOn) {
    if (addOn.quantity === undefined || addOn.quantityLocked) {
      return;
    }
    addOn.quantity += 1;
  }

  decrementAddOnQuantity(addOn: MarketplaceAddOn) {
    if (addOn.quantity === undefined || addOn.quantity <= 1 || addOn.quantityLocked) {
      return;
    }
    addOn.quantity -= 1;
  }

  addToPlan(addOn: MarketplaceAddOn) {
    if (
      addOn.buttonDisabled ||
      addOn.isSubscribed ||
      !this.restaurantId ||
      addOn.actionInProgress
    ) {
      return;
    }
    // Hardware is only requested here: nothing is charged until our team delivers it,
    // so no card is needed yet (FNF-1454). Nor while a promotion covers billing: add-ons
    // are billed after the month, and promo days are free (FNF-1509).
    if (
      !addOn.isHardware &&
      addOn.price > 0 &&
      !this.promoCoversBilling &&
      !this.currentPaymentMethod?.hasPaymentMethod
    ) {
      this.toastr.info(
        'Add a payment method before selecting a paid add-on.',
        'Payment method needed',
      );
      this.addOrUpdatePaymentMethod();
      return;
    }
    const payload = {
      addonId: addOn.addonId,
      quantity: addOn.quantity ?? 1,
    };
    addOn.actionInProgress = true;
    this.subscriptionService.includeAddonsWithSubscription(payload).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          addOn.actionInProgress = false;
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          addOn.actionInProgress = false;
          this.toastr.error('No data found!', 'Error');
          return;
        }
        if (addOn.isHardware) {
          this.toastr.success(
            `Our team will deliver your ${addOn.name}. ${this.billedFromLabel('delivery')}`,
            'Hardware requested',
          );
        } else {
          this.toastr.success(
            `${addOn.name} added to your plan. ${this.billedFromLabel('today')}`,
            'Add-on added',
          );
        }
        this.getCurrentSubscription();
        this.getCurrentSubscriptionInvoices();
        this.getNextInvoice();
      },
      error: () => {
        addOn.actionInProgress = false;
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  /** "Request 2 more" when raising delivered hardware, else "Update Add-on" (FNF-1464). */
  quantityButtonLabel(addOn: MarketplaceAddOn): string {
    const extra = (addOn.quantity ?? 0) - (addOn.subscribedQuantity ?? 0);
    if (addOn.isHardware && extra > 0) return `Request ${extra} more`;
    // Fewer devices are collected by our team first (FNF-1465).
    if (addOn.isHardware && extra < 0) return `Request return of ${-extra}`;
    return 'Update Add-on';
  }

  removeButtonLabel(addOn: MarketplaceAddOn): string {
    return addOn.isHardware ? 'Request return' : 'Remove Add-on';
  }

  updateAddonQuantity(addOn: MarketplaceAddOn) {
    if (!this.restaurantId || !addOn.isSubscribed || addOn.actionInProgress) {
      return;
    }
    // More hardware is a request: nothing is billed until it is delivered (FNF-1464).
    const isHardwareIncrease =
      !!addOn.isHardware && (addOn.quantity ?? 0) > (addOn.subscribedQuantity ?? 0);
    // Fewer is a return: still billed until our team collects it (FNF-1465).
    const isHardwareReturn =
      !!addOn.isHardware && (addOn.quantity ?? 0) < (addOn.subscribedQuantity ?? 0);
    if (
      !isHardwareIncrease &&
      addOn.price > 0 &&
      !this.promoCoversBilling &&
      !this.currentPaymentMethod?.hasPaymentMethod
    ) {
      this.toastr.info(
        'Add a payment method before increasing a paid add-on.',
        'Payment method needed',
      );
      this.addOrUpdatePaymentMethod();
      return;
    }
    const payload = { quantity: addOn.quantity ?? 1 };
    addOn.actionInProgress = true;
    this.subscriptionService.updateSubscriptionAddonQuantity(addOn.addonId, payload).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          addOn.actionInProgress = false;
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          addOn.actionInProgress = false;
          this.toastr.error('No data found!', 'Error');
          return;
        }
        if (isHardwareIncrease) {
          this.toastr.success(
            `Our team will deliver the extra ${addOn.name}. ${this.billedFromLabel('delivery')}`,
            'Hardware requested',
          );
        } else if (isHardwareReturn) {
          this.toastr.success(
            `Our team will collect the ${addOn.name}. It stays billed until collected.`,
            'Return requested',
          );
        } else {
          this.toastr.success(`${addOn.name} quantity updated.`, 'Add-on updated');
        }
        this.getCurrentSubscription();
        this.getCurrentSubscriptionInvoices();
        this.getNextInvoice();
      },
      error: () => {
        addOn.actionInProgress = false;
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  /** Withdraw a hardware request that hasn't been delivered (FNF-1454). */
  cancelHardwareRequest(addOn: MarketplaceAddOn) {
    if (
      !this.restaurantId ||
      addOn.actionInProgress ||
      (addOn.requestStatus !== 'REQUESTED' && addOn.requestStatus !== 'ON_HOLD')
    ) {
      return;
    }
    addOn.actionInProgress = true;
    // More units of delivered hardware are withdrawn by setting the quantity back; a
    // first request is removed (FNF-1464).
    const request$ = addOn.isSubscribed
      ? this.subscriptionService.updateSubscriptionAddonQuantity(addOn.addonId, {
          quantity: addOn.subscribedQuantity ?? 1,
        })
      : this.subscriptionService.removeSubscriptionAddon(addOn.addonId);
    request$.subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data) {
          addOn.actionInProgress = false;
          this.toastr.error(
            response.error?.error?.message || response.error?.message || 'No data found!',
            'Error',
          );
          return;
        }
        this.toastr.success(
          addOn.isReturn
            ? `You're keeping your ${addOn.name}.`
            : `Your ${addOn.name} request was canceled.`,
          addOn.isReturn ? 'Return canceled' : 'Request canceled',
        );
        this.getCurrentSubscription();
      },
      error: () => {
        addOn.actionInProgress = false;
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  removeAddon(addOn: MarketplaceAddOn) {
    if (!this.restaurantId || !addOn.isSubscribed || addOn.actionInProgress) {
      return;
    }
    addOn.actionInProgress = true;
    this.subscriptionService.removeSubscriptionAddon(addOn.addonId).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          addOn.actionInProgress = false;
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          addOn.actionInProgress = false;
          this.toastr.error('No data found!', 'Error');
          return;
        }
        if (addOn.isHardware) {
          // Hardware is collected by our team first and billed until then (FNF-1465).
          this.toastr.success(
            `Our team will collect your ${addOn.name}. It stays billed until collected.`,
            'Return requested',
          );
        } else {
          this.toastr.success(`${addOn.name} removed from your plan.`, 'Add-on removed');
        }
        this.getCurrentSubscription();
        this.getCurrentSubscriptionInvoices();
        this.getNextInvoice();
      },
      error: () => {
        addOn.actionInProgress = false;
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  openInvoice(invoice: BillingHistoryRow) {
    if (!invoice.hostedUrl) {
      this.toastr.info('This invoice is not available to view yet.', 'View unavailable');
      return;
    }
    // hostedUrl is Stripe's hosted invoice page (viewable HTML) — pdfUrl is the raw file and
    // always forces a download (Content-Disposition: attachment), even in a new tab.
    window.open(invoice.hostedUrl, '_blank');
  }

  downloadInvoice(invoice: BillingHistoryRow) {
    if (!invoice.pdfUrl) {
      this.toastr.info("A PDF isn't available for this invoice yet.", 'Download unavailable');
      return;
    }
    window.open(invoice.pdfUrl, '_blank');
  }

  viewNextInvoice() {
    if (!this.nextInvoicePreview) {
      return;
    }
    this.dialog.open(NextInvoicePreviewComponent, {
      width: '480px',
      maxWidth: '95vw',
      data: this.nextInvoicePreview,
    });
  }

  openManagePlan() {
    const dialogRef = this.dialog.open(ManagePlanComponent, {
      width: '1100px',
      maxWidth: '95vw',
      data: {
        plans: this.plans?.plans || [],
        currentPlanName: this.currentSubscriptionPlan?.name || '',
        currency: this.currency,
        hasPaymentMethod: !!this.currentPaymentMethod?.hasPaymentMethod,
        canManagePlans: this.canManagePlans,
        posProvider: this.currentRestaurant?.pos_provider,
        promoEndsAt: this.currentSubscription?.promoEndsAt,
        pendingPlanId: this.currentSubscription?.pendingPlanId,
        downgradeEffectiveLabel: this.downgradeEffectiveLabel,
      },
    });

    dialogRef.afterClosed().subscribe((result: boolean | typeof NEEDS_PAYMENT_METHOD) => {
      if (result === NEEDS_PAYMENT_METHOD) {
        this.addOrUpdatePaymentMethod();
      } else if (result) {
        this.getCurrentSubscription();
        this.getCurrentSubscriptionInvoices();
        this.getNextInvoice();
      }
    });
  }

  getAllPlans() {
    this.displayLoading = true;
    this.subscriptionService.getSubscriptionPlans().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.plans = response.data;
        this.displayLoading = false;
        this.currentSubscriptionPlan = this.plans?.plans?.find(
          (e: any) => e.planId == this.currentSubscription?.planId,
        );
        // Keeps the sidebar/route-guard's plan-tier gate in sync right after a plan
        // change succeeds (openManagePlan() re-triggers this same load path) — without
        // this, an upgrade/downgrade wouldn't unlock/lock menus until a page reload.
        if (this.currentSubscriptionPlan?.name) {
          this.restaurantService.currentPlanName$.next(this.currentSubscriptionPlan.name);
        }
        this.getAllAddons();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  getAllAddons() {
    this.displayLoading = true;
    this.subscriptionService.getSubscriptionAddons().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.addons1 = response.data;
        this.displayLoading = false;
        this.buildAddOnsMarketplace();
        this.buildCurrentSubscriptionAddons();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  getCurrentSubscription() {
    this.displayLoading = true;
    this.subscriptionService.getMySubscription().subscribe({
      next: (response: any) => {
        if (!response.data && response.error) {
          if (response.status === 404) {
            this.subscriptionNotFound = true;
            this.subscriptionLoaded = true;
            this.displayLoading = false;
            return;
          }
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.subscriptionLoaded = true;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.subscriptionLoaded = true;
          this.displayLoading = false;
          return;
        }
        this.subscriptionNotFound = false;
        this.subscriptionLoaded = true;
        this.currentSubscription = response.data;
        // Older invoices are recognised by the subscription's start (FNF-1325), which may
        // arrive after them.
        this.buildBillingHistory();
        this.getAllPlans();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.subscriptionLoaded = true;
        this.displayLoading = false;
      },
    });
  }

  private buildAddOnsMarketplace() {
    const currentPlanId = (this.plans?.plans || []).find(
      (p: any) => p.planId === this.currentSubscriptionPlan?.planId,
    )?.planId;
    const addons = this.addons1?.addons || [];

    if (!currentPlanId || !addons.length) {
      return;
    }

    const subscribedAddons = this.currentSubscription?.addOns || [];
    const requests = this.currentSubscription?.addOnRequests || [];

    this.addOnsMarketplace = addons.map((addon: any) => {
      const isCompatible =
        !addon.compatiblePlanIds?.length || addon.compatiblePlanIds.includes(currentPlanId);
      const subscribed = subscribedAddons.find((s: any) => s.addonId === addon.addonId);
      const request = requests.find((r: any) => r.addonId === addon.addonId);
      return this.toMarketplaceAddOn(addon, isCompatible, subscribed, request);
    });
  }

  // A plain request has no badge: its "Cancel request" button already says so.
  private static readonly REQUEST_BADGES: Partial<
    Record<HardwareRequestStatus, { label: string; type: MarketplaceBadgeType }>
  > = {
    ON_HOLD: { label: 'On hold', type: 'on-hold' },
    REJECTED: { label: 'Not approved', type: 'rejected' },
  };

  private static readonly INVOICE_STATUS_META: Record<
    string,
    { status: BillingHistoryStatus; label: string }
  > = {
    PAID: { status: 'paid', label: 'Paid' },
    DRAFT: { status: 'draft', label: 'Pending' },
    VOID: { status: 'void', label: 'Void' },
    UNCOLLECTIBLE: { status: 'uncollectible', label: 'Uncollectible' },
  };

  private buildBillingHistory() {
    // The $0 invoice Stripe raises when the subscription starts isn't a charge; it stays
    // in the ledger but isn't listed (FNF-1325).
    const createdAt = this.currentSubscription?.createdAt;
    const invoices = (this.currentInvoices || []).filter(
      (invoice: any) => !isZeroOpeningInvoice(invoice, createdAt),
    );

    this.billingHistory = invoices.map((invoice: any) => {
      const meta =
        SubscriptionComponent.INVOICE_STATUS_META[invoice.status] ||
        SubscriptionComponent.INVOICE_STATUS_META['DRAFT'];
      const amountCents =
        invoice.status === 'PAID' ? invoice.amountPaidCents : invoice.amountDueCents;
      const items: BillingHistoryItem[] = (invoice.lineItems || []).map((li: any) => ({
        type: li.type === 'ADDON' || li.type === 'USAGE' ? li.type : 'PLAN',
        label: `${li.description}${li.quantity > 1 && !hasQuantityInLabel(li.description) ? ` x${li.quantity}` : ''}${li.isProration ? ' (Proration)' : ''}`,
      }));

      return {
        invoiceId: invoice.invoiceId || '',
        invoiceNumber: `INV-${(invoice.invoiceId || '').slice(0, 8).toUpperCase()}`,
        billingPeriod: `${this.convertSecondsToRestaurantTime(invoice.periodStart)} - ${this.convertSecondsToRestaurantTime(invoice.periodEnd)}`,
        invoiceDate: this.convertSecondsToRestaurantTime(invoice.issuedAt),
        items,
        amount: (amountCents ?? 0) / 100,
        status: meta.status,
        statusLabel: meta.label,
        pdfUrl: invoice.externalInvoicePdfUrl,
        hostedUrl: invoice.externalInvoiceHostedUrl,
      };
    });
    this.focusHighlightedInvoice();
  }

  private buildCurrentSubscriptionAddons() {
    const subscribedAddons = this.currentSubscription?.addOns || [];
    const catalog = this.addons1?.addons || [];

    this.currentSubscriptionAddons = subscribedAddons.map((subscribed: any) => {
      const catalogAddon = catalog.find((a: any) => a.addonId === subscribed.addonId);
      const meta = catalogAddon
        ? this.addOnCategoryMeta[catalogAddon.category] || this.addOnCategoryMeta['SOFTWARE']
        : null;
      const unitPrice = catalogAddon ? catalogAddon.currentPrice.amountCents / 100 : 0;
      const quantity = subscribed.quantity ?? 1;

      return {
        name: catalogAddon?.name || subscribed.addonId,
        description: `${quantity} × ${this.getCurrencySymbol(this.currency)}${unitPrice.toFixed(2)}${meta ? ' · ' + meta.label : ''}`,
        price: unitPrice * quantity,
        status: 'success',
        statusLabel: 'Active',
      };
    });

    // Hardware waiting for delivery: listed, but not charged until it arrives (FNF-1454).
    const openRequests = (this.currentSubscription?.addOnRequests || []).filter(
      (r: any) => r.status === 'REQUESTED' || r.status === 'ON_HOLD',
    );
    for (const request of openRequests) {
      const catalogAddon = catalog.find((a: any) => a.addonId === request.addonId);
      const unitPrice = catalogAddon ? catalogAddon.currentPrice.amountCents / 100 : 0;
      const quantity = request.quantity ?? 1;
      this.currentSubscriptionAddons.push({
        name: catalogAddon?.name || request.addonId,
        description: `${quantity} × ${this.getCurrencySymbol(this.currency)}${unitPrice.toFixed(2)} · billing starts on delivery`,
        price: unitPrice * quantity,
        status: 'warning',
        statusLabel: request.status === 'ON_HOLD' ? 'On hold' : 'Requested',
      });
    }
  }

  private toMarketplaceAddOn(
    addon: any,
    isCompatible: boolean,
    subscribed?: {
      quantity?: number;
      requestStatus?: string;
      requestedQuantity?: number;
      statusReason?: string;
    },
    request?: { status?: string; statusReason?: string; quantity?: number },
  ): MarketplaceAddOn {
    const meta = this.addOnCategoryMeta[addon.category] || this.addOnCategoryMeta['SOFTWARE'];
    const billingLabel = addon.billingModel === 'PER_UNIT' ? 'Per-unit' : 'Flat';
    const priceUnit =
      addon.billingModel === 'PER_UNIT' ? `${addon.unitLabel || 'unit'}/month` : 'per month';
    const isSubscribed = !!subscribed;
    const subscribedQuantity = isSubscribed ? (subscribed?.quantity ?? 1) : undefined;
    // Quantity is only ever editable for PER_UNIT add-ons — FLAT-billed ones must stay at 1
    // (the backend rejects any other quantity), so no stepper for them regardless of subscription state.
    const isHardware = addon.category === 'HARDWARE';
    // A first request, or more units asked for on delivered hardware (FNF-1464).
    const requestStatus = (isSubscribed ? subscribed?.requestStatus : request?.status) as
      HardwareRequestStatus | undefined;
    const requestReason =
      (isSubscribed ? subscribed?.statusReason : request?.statusReason) || undefined;
    const requestedQuantity = isSubscribed ? subscribed?.requestedQuantity : undefined;
    const isReturn =
      requestedQuantity != null &&
      subscribedQuantity != null &&
      requestedQuantity < subscribedQuantity;
    const isOpenRequest = requestStatus === 'REQUESTED' || requestStatus === 'ON_HOLD';
    // While a change to delivered hardware is open, the card shows what is delivered and
    // billed, not the number asked for: nothing has changed yet (FNF-1465).
    const pendingOnDelivered = isSubscribed && isOpenRequest;
    const quantity =
      addon.billingModel === 'PER_UNIT'
        ? isSubscribed
          ? (subscribedQuantity ?? 1)
          : isOpenRequest
            ? (request?.quantity ?? 1)
            : 1
        : undefined;
    const held = subscribedQuantity ?? 1;
    let pendingNote: string | undefined;
    if (pendingOnDelivered && requestedQuantity != null) {
      if (isReturn) {
        pendingNote =
          requestedQuantity === 0
            ? `Returning all ${held} · billed until our team collects them`
            : `Returning ${held - requestedQuantity} of ${held} · all ${held} billed until collected`;
      } else {
        pendingNote = `+${requestedQuantity - held} requested · ${held} billed until delivered`;
      }
    }
    const badge = isReturn
      ? {
          label: requestStatus === 'ON_HOLD' ? 'Return on hold' : 'Return requested',
          type: (requestStatus === 'ON_HOLD' ? 'on-hold' : 'requested') as MarketplaceBadgeType,
        }
      : requestStatus === 'REQUESTED' && requestedQuantity && subscribedQuantity
        ? {
            label: `+${requestedQuantity - subscribedQuantity} requested`,
            type: 'requested' as const,
          }
        : requestStatus
          ? SubscriptionComponent.REQUEST_BADGES[requestStatus]
          : undefined;
    const buttonLabel = !isCompatible
      ? 'Not available on your plan'
      : isHardware
        ? requestStatus === 'REJECTED'
          ? 'Request again'
          : 'Request'
        : 'Add to Plan';

    return {
      addonId: addon.addonId,
      name: addon.name,
      description: addon.description || `${meta.label} add-on for your restaurant.`,
      category: `${meta.label} · ${billingLabel}`,
      icon: meta.icon,
      gradient: meta.gradient,
      iconColor: meta.iconColor,
      price: addon.currentPrice.amountCents / 100,
      priceUnit,
      quantity,
      isSubscribed,
      subscribedQuantity,
      buttonLabel,
      buttonStyle: isCompatible ? 'dark' : 'outline',
      buttonDisabled: !isCompatible,
      isHardware,
      requestStatus,
      requestReason,
      requestedQuantity,
      isReturn,
      quantityLocked: isOpenRequest,
      pendingNote,
      badge,
    };
  }

  convertSecondsToRestaurantTime(seconds: any): string {
    if (seconds === null || seconds === undefined || isNaN(seconds)) {
      return '';
    }
    return new Date(Number(seconds) * 1000).toLocaleDateString('en-US', {
      timeZone: this.currentRestaurant?.time_zone || 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  getCurrentPaymentMethod() {
    this.displayLoading = true;
    this.subscriptionService.getMyPaymentMethod().subscribe({
      next: (response: any) => {
        if (!response.data && response.error) {
          if (response.status === 404) {
            this.displayLoading = false;
            return;
          }
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.currentPaymentMethod = response.data;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  getCurrentSubscriptionInvoices() {
    this.displayLoading = true;
    this.subscriptionService.getMyInvoices().subscribe({
      next: (response: any) => {
        if (!response.data && response.error) {
          if (response.status === 404) {
            this.displayLoading = false;
            return;
          }
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.currentInvoices = response.data?.invoices;
        this.buildBillingHistory();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  getNextInvoice() {
    this.getUsage();
    this.subscriptionService.getMyNextInvoice().subscribe({
      next: (response: any) => {
        if (!response.data) {
          // No preview available (billing not enabled, or no Stripe subscription yet) — the
          // template just hides the "Upcoming" section rather than showing a stale/fake amount.
          return;
        }
        this.nextInvoicePreview = response.data;
      },
      error: () => {},
    });
  }

  /** Refreshed with the invoice preview: both change whenever the plan or period does. */
  getUsage() {
    this.subscriptionService.getMyUsage().subscribe({
      next: (response: any) => {
        this.usage = response?.data?.orders != null ? response.data : null;
      },
      error: () => {},
    });
  }

  get usageEstimateLabel(): string {
    return this.usage
      ? priceLabel({ amountCents: this.usage.estimatedAmountCents, currency: this.usage.currency })
          .amount
      : '';
  }

  get usageAddOnsLabel(): string {
    return this.usage
      ? priceLabel({
          amountCents: this.usage.addOnsEstimatedAmountCents ?? 0,
          currency: this.usage.currency,
        }).amount
      : '';
  }

  get isCancellationScheduled(): boolean {
    return !!this.currentSubscription?.cancelAtPeriodEnd;
  }

  /** "$0.50/order" or "$49.99/mon" for the current plan card (FNF-1286). */
  get currentPriceLabel(): PriceLabel {
    return priceLabel(this.currentSubscriptionPlan?.currentPrice);
  }

  /** The plan a scheduled downgrade switches to at period end, if any (FNF-1297). */
  get pendingPlan(): any {
    const pendingPlanId = this.currentSubscription?.pendingPlanId;
    if (!pendingPlanId) {
      return null;
    }
    return this.plans?.plans?.find((p: any) => p.planId === pendingPlanId) ?? null;
  }

  /** When a downgrade chosen now would apply: the end of the running period. */
  get downgradeEffectiveLabel(): string {
    const periodEnd = this.currentSubscription?.currentPeriodEnd;
    if (!periodEnd) {
      return '';
    }
    return this.convertSecondsToRestaurantTime(periodEndAfter(periodEnd, Date.now() / 1000));
  }

  cancelPendingPlanChange() {
    if (this.cancellingPendingPlan) {
      return;
    }
    this.cancellingPendingPlan = true;
    this.subscriptionService.cancelPendingPlanChange().subscribe({
      next: (response: FNFResponse<any>) => {
        this.cancellingPendingPlan = false;
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          return;
        }
        this.toastr.success(
          `You'll stay on ${this.currentSubscriptionPlan?.name || 'your current plan'}.`,
          'Scheduled change cancelled',
        );
        this.getCurrentSubscription();
        this.getNextInvoice();
      },
      error: () => {
        this.cancellingPendingPlan = false;
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  get hasActivePromotion(): boolean {
    return !!this.currentSubscription?.activePromotionId;
  }

  /** A promotion still covers billing: add-ons cost nothing until it ends and need no card
   * yet, as the server's own card check (FNF-1509). */
  get promoCoversBilling(): boolean {
    const promoEndsAt = this.currentSubscription?.promoEndsAt;
    return !!promoEndsAt && promoEndsAt > Date.now() / 1000;
  }

  /** Add-ons are billed after each month for the days they were active, promo days free
   * (FNF-1507). */
  billedFromLabel(start: 'today' | 'delivery'): string {
    if (this.promoCoversBilling) {
      const until = this.convertSecondsToRestaurantTime(this.currentSubscription?.promoEndsAt);
      return `Free until ${until}, then billed at the end of each month for the days you use it.`;
    }
    return start === 'delivery'
      ? 'Billed from delivery, at the end of each month.'
      : 'Billed at the end of each month for the days you use it.';
  }

  /** The short billing line under an add-on card's price. */
  addOnBillingHint(addOn: MarketplaceAddOn): string | null {
    if (addOn.lockedMessage || addOn.pendingNote) {
      return null;
    }
    const until = this.promoCoversBilling
      ? this.convertSecondsToRestaurantTime(this.currentSubscription?.promoEndsAt)
      : null;
    if (addOn.isSubscribed) {
      return until ? `Free until ${until} (promotion)` : null;
    }
    if (addOn.isHardware) {
      if (addOn.buttonDisabled) return null;
      return until
        ? `Delivered by our team · free until ${until}`
        : 'Delivered by our team · billed from delivery, at month end';
    }
    return until
      ? `Free until ${until} · then billed for the days you use it`
      : 'Billed at month end for the days you use it';
  }

  /** Add-on charges so far in the upcoming invoice, billed after the month (FNF-1507). */
  get nextInvoiceAddOnsCents(): number {
    return (this.nextInvoicePreview?.lineItems || [])
      .filter((item: any) => item.type === 'ADDON')
      .reduce((sum: number, item: any) => sum + (item.amountCents || 0), 0);
  }

  get isPastDue(): boolean {
    return this.currentSubscription?.status === 'PAST_DUE';
  }

  /** A created or published (not yet activated) restaurant can view this page
   * (see RESTAURANT_STATUS_MENU_TITLES / PUBLISHED_STATUS_ROUTES) but can't
   * change plans until it's fully activated. */
  get canManagePlans(): boolean {
    return !isLimitedAccessStatus(this.currentRestaurant?.status);
  }

  get isLimitedAccess(): boolean {
    return isLimitedAccessStatus(this.currentRestaurant?.status);
  }

  addOrUpdatePaymentMethod() {
    this.redirectTo(
      this.subscriptionService.createPaymentMethodCheckoutSession(this.returnUrl()),
      'Could not start the secure payment form. Please try again.',
    );
  }

  manageBilling(): void {
    this.redirectTo(
      this.subscriptionService.createBillingPortalSession(this.returnUrl()),
      'Could not open the billing portal. Please try again.',
    );
  }

  private redirectTo(request: any, failureMessage: string): void {
    this.redirecting = true;
    request.subscribe((response: any) => {
      const url = response?.data?.url;
      if (url) {
        window.location.assign(url);
        return;
      }
      this.redirecting = false;
      if (response?.status === 501) {
        this.billingUnavailable = true;
        this.toastr.info('Online billing is not enabled for your account yet.', 'Billing');
        return;
      }
      this.toastr.error(failureMessage, 'Billing');
    });
  }

  private returnUrl(): string {
    return `${window.location.origin}/main/billing-and-subscription`;
  }
}
